import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { ConfirmPopoverComponent } from './confirm-popover.component';

@Directive({
  selector: '[lyConfirm]',
  standalone: true,
})
export class ConfirmDirective {
  private overlayRef!: OverlayRef;
  private scrollHandler?: () => void;

  @Input() lyConfirmDisabled = false;

  @Input()
  lyConfirmTitle = 'Are you sure?';

  @Input()
  lyConfirmContent = "Just making sure - this can't be undone. Continue?";

  @Input()
  lyConfirmBtnLabel = 'Yep, go ahead';

  @Input()
  lyConfirmBtnClasses = 'button indigo';

  @Input()
  lyConfirmHasRejectBtn = false;

  @Input()
  lyConfirmRejectBtnLabel = 'Wait, go back';

  @Input()
  lyConfirmRejectBtnClasses = 'button';

  @Input()
  lyConfirmCalloutContent = '';

  @Output()
  confirm = new EventEmitter();

  @Output()
  reject = new EventEmitter();

  @HostListener('click')
  onClick() {
    if (this.lyConfirmDisabled) return;
    this.show();
  }

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly injector: Injector,
  ) {}

  show() {
    this.createOverlay();
  }

  close() {
    this.disposeOverlay();
  }

  handleConfirm() {
    this.confirm.emit();
    this.close();
  }

  handleReject() {
    this.reject.emit();
    this.close();
  }

  private createOverlay() {
    // Scroll strategy
    const scrollStrategy = this.overlay.scrollStrategies.block();

    // Position strategy - center in the middle of the page
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    // Create overlay with backdrop
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'confirm-popover-backdrop',
      positionStrategy,
      scrollStrategy,
      panelClass: 'confirm-popover-panel',
    });

    // Create ComponentPortal and attach it to the overlay
    const portal = new ComponentPortal(
      ConfirmPopoverComponent,
      this.viewContainerRef,
      this.injector,
    );

    const componentRef = this.overlayRef.attach(portal);

    // Set properties on the attached component
    componentRef.instance.title = this.lyConfirmTitle;
    componentRef.instance.content = this.lyConfirmContent;
    componentRef.instance.confirmBtnLabel = this.lyConfirmBtnLabel;
    componentRef.instance.confirmBtnClasses = this.lyConfirmBtnClasses;
    componentRef.instance.confirmHasRejectBtn = this.lyConfirmHasRejectBtn;
    componentRef.instance.confirmRejectBtnLabel = this.lyConfirmRejectBtnLabel;
    componentRef.instance.confirmRejectBtnClasses =
      this.lyConfirmRejectBtnClasses;
    componentRef.instance.confirmCalloutContent = this.lyConfirmCalloutContent;

    // Set up event subscriptions
    componentRef.instance.close.subscribe(() => this.close());
    componentRef.instance.confirm.subscribe(() => this.handleConfirm());
    componentRef.instance.reject.subscribe(() => this.handleReject());

    // Force change detection to ensure the component renders
    componentRef.changeDetectorRef.detectChanges();

    // Add the proper CSS class for overlay positioning
    const componentElement = componentRef.location.nativeElement;
    componentElement.classList.add('confirm-popover-overlay');

    // Ensure the component is visible and properly positioned
    componentElement.style.position = 'fixed';
    componentElement.style.zIndex = '9999';

    // Calculate initial centered position
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Force a layout calculation to get component dimensions
    componentElement.style.visibility = 'hidden';
    componentElement.style.display = 'block';
    const componentRect = componentElement.getBoundingClientRect();
    const componentWidth = componentRect.width;
    const componentHeight = componentRect.height;
    componentElement.style.visibility = 'visible';

    // Calculate centered position
    const centerX = (viewportWidth - componentWidth) / 2;
    const centerY = (viewportHeight - componentHeight) / 2;

    // Apply centered positioning
    componentElement.style.top = `${centerY}px`;
    componentElement.style.left = `${centerX}px`;

    // Add fallback styles to ensure visibility (in case CSS variables are missing)
    const popoverElement = componentElement.querySelector('.confirm-popover');
    if (popoverElement) {
      (popoverElement as HTMLElement).style.boxShadow =
        '0 4px 20px rgba(0, 0, 0, 0.35)';
    }

    // Style the overlay panel to ensure it's visible and centered
    const overlayElement = this.overlayRef.overlayElement;
    if (overlayElement) {
      overlayElement.style.position = 'fixed';
      overlayElement.style.top = '0';
      overlayElement.style.left = '0';
      overlayElement.style.width = '100%';
      overlayElement.style.height = '100%';
      overlayElement.style.display = 'flex';
      overlayElement.style.alignItems = 'center';
      overlayElement.style.justifyContent = 'center';
      overlayElement.style.zIndex = '9998';
    }

    // Add a custom backdrop element for better visibility
    const backdropElement = document.createElement('div');
    backdropElement.className = 'custom-backdrop';
    backdropElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(1px);
      z-index: 9997;
      pointer-events: auto;
    `;

    // Prevent click event from bubbling up to the overlay
    backdropElement.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    document.body.appendChild(backdropElement);

    // Add scroll event listener to update popover position when scrolling
    const handleScroll = () => {
      // Manually recalculate centered position when scrolling
      if (componentElement && overlayElement) {
        // Get current viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Get component dimensions
        const componentRect = componentElement.getBoundingClientRect();
        const componentWidth = componentRect.width;
        const componentHeight = componentRect.height;

        // Calculate centered position
        const centerX = (viewportWidth - componentWidth) / 2;
        const centerY = (viewportHeight - componentHeight) / 2;

        // Apply centered positioning
        componentElement.style.position = 'fixed';
        componentElement.style.top = `${centerY}px`;
        componentElement.style.left = `${centerX}px`;
        componentElement.style.transform = 'none';
      }
    };

    // Add scroll listeners to window and document
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });

    // Store the scroll handler reference for cleanup
    this.scrollHandler = handleScroll;

    // Subscribe to overlay events
    this.overlayRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });
  }

  private disposeOverlay() {
    if (this.overlayRef) {
      // Remove custom backdrop if it exists
      const customBackdrop = document.querySelector('.custom-backdrop');
      if (customBackdrop) {
        customBackdrop.remove();
      }

      this.overlayRef.dispose();
    }
    // Clean up scroll listeners
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      document.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = undefined;
    }
  }
}
